import React, { useEffect, useState, useRef, useCallback } from "react";
import { Col, Card, Button, Row, Modal, ProgressBar } from "react-bootstrap";
import { useHistory, useLocation } from "react-router-dom/cjs/react-router-dom.min";
import { useGetAllCommonStudentInitialDataMutation, usePostCommonMutation } from "slices/get/common";
import useService from "views/default/useService";
import './assessment-timer.css';
import { BackButton, LoadingSpinner, NoDataFound } from './CommonComponents';
import './common-components.css';

function AssessmentComponent() {
  const location = useLocation();

  const query = new URLSearchParams(location.search);
  const assessmentId = query.get('assessmentId');
  const durationFromUrl = query.get('duration'); // Get duration from URL parameter
  const [getAllCommonStudentInitialData] = useGetAllCommonStudentInitialDataMutation();
  const { checkAccessTime, fetchAccessToken } = useService();
  const [quizQuestions, setQuestionsData] = useState([]);
  const [assessmentInfo, setAssessmentInfo] = useState(null);
  const [index, setIndex] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const currentPath = location.pathname;
  const [postCommon] = usePostCommonMutation();
  const access_token = fetchAccessToken();
  let studentData = sessionStorage.getItem('studentData')
  let student = JSON.parse(studentData)
  const history = useHistory()

  // Timer states
  const [timerStarted, setTimerStarted] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(0); // in seconds
  const [totalDuration, setTotalDuration] = useState(0); // in seconds
  const [showTimeUpModal, setShowTimeUpModal] = useState(false);
  const [isAutoSubmitting, setIsAutoSubmitting] = useState(false);
  const timerRef = useRef(null);
  const hasAutoSubmitted = useRef(false);
  const startTimeRef = useRef(null); // Track when the exam started

  useEffect(() => {
    setIsLoading(true);
    getAllCommonStudentInitialData({ ep: `student/assessment_questions?assessment_id=${assessmentId}&type=assessment`, token: access_token }).then(
      (response) => {
        if (response && response?.data && response.data.status) {
          setQuestionsData(response.data.data);
          // Get assessment duration - prioritize URL parameter, then API response, then default
          if (response.data.data.length > 0) {
            const duration = durationFromUrl 
              ? parseInt(durationFromUrl) 
              : (response.data.data[0].assessmentDuration || response.data.assessmentDuration || 30);
            const durationInSeconds = duration * 60;
            setTotalDuration(durationInSeconds);
            setTimeRemaining(durationInSeconds);
            setAssessmentInfo({
              duration: duration,
              totalQuestions: response.data.data.length,
              assessmentName: response.data.data[0].assessment_name || 'Assessment'
            });
          }
        } else {
          history.push(`/viewResults?assessment_id=${assessmentId}`)
        }
        setIsLoading(false);
        checkAccessTime(response, currentPath);
      }
    );

    // Cleanup timer on unmount
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, []);

  // Timer effect
  useEffect(() => {
    if (timerStarted && timeRemaining > 0 && !isSubmitted) {
      timerRef.current = setInterval(() => {
        setTimeRemaining((prev) => {
          if (prev <= 1) {
            clearInterval(timerRef.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timerRef.current);
    }
  }, [timerStarted, isSubmitted]);

  // Auto-submit when time runs out
  useEffect(() => {
    if (timeRemaining === 0 && timerStarted && !isSubmitted && !hasAutoSubmitted.current) {
      hasAutoSubmitted.current = true;
      setShowTimeUpModal(true);
      handleAutoSubmit();
    }
  }, [timeRemaining, timerStarted, isSubmitted]);

  // Format time as MM:SS
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  };

  // Get timer color based on remaining time
  const getTimerColor = () => {
    const percentageLeft = (timeRemaining / totalDuration) * 100;
    if (percentageLeft <= 10) return 'danger';
    if (percentageLeft <= 25) return 'warning';
    return 'success';
  };

  // Get timer class for animation
  const getTimerClass = () => {
    const percentageLeft = (timeRemaining / totalDuration) * 100;
    if (percentageLeft <= 10) return 'timer-critical';
    if (percentageLeft <= 25) return 'timer-warning';
    return 'timer-normal';
  };

  const handleOptionClick = (questionId, optionIndex) => {
    // Start timer on first option click
    if (!timerStarted) {
      setTimerStarted(true);
      startTimeRef.current = Date.now(); // Record start time
    }

    setSelectedAnswers({
      ...selectedAnswers,
      [questionId]: optionIndex,
    });
  };

  // Calculate spending time in seconds
  const calculateSpendingTime = () => {
    if (!startTimeRef.current) return 0;
    const endTime = Date.now();
    const spendingTimeInSeconds = Math.floor((endTime - startTimeRef.current) / 1000);
    return spendingTimeInSeconds;
  };

  // Format spending time as HH:MM:SS or MM:SS
  const formatSpendingTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    if (hours > 0) {
      return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
    }
    return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  };

  const changeIndex = () => {
    if (index < quizQuestions.length - 1) {
      setIndex(index + 1);
    }
  };

  const goToQuestion = (questionIndex) => {
    setIndex(questionIndex);
  };

  const calculateScore = () => {
    return quizQuestions.reduce((score, question) => {
      if (selectedAnswers[question.id] === parseInt(question.answer)) {
        return score + 1;
      }
      return score;
    }, 0);
  };

  const countAnsweredQuestions = () => {
    return Object.keys(selectedAnswers).length;
  };

  const handleAutoSubmit = async () => {
    setIsAutoSubmitting(true);
    await submitAnswers();
    setIsAutoSubmitting(false);
    setIsSubmitted(true);
  };

  const submitAnswers = async () => {
    const submittedAnswers = quizQuestions.map((question) => ({
      questionId: question.id,
      givenAnswer: selectedAnswers[question.id],
      correctAnswer: parseInt(question.answer),
    }));

    const totalScore = calculateScore();
    const answeredCount = countAnsweredQuestions();
    const spendingTime = calculateSpendingTime(); // Calculate time spent

    const values = {
      answers: submittedAnswers,
      totalScore: totalScore,
      answeredCount: answeredCount,
      assessment_id: assessmentId,
      assessment_type: quizQuestions[0].assessment_type,
      student_id: student[0].student_id,
      spendingTime: spendingTime, // Time in seconds
      spendingTimeFormatted: formatSpendingTime(spendingTime) // Formatted time string (MM:SS or HH:MM:SS)
    };

    await postCommon({ ep: `assessment/create_assessment_question_answers`, values, token: access_token }).then(
      (response) => {
        if (response && response?.data && response.data.status) {
          // Success
        }
        checkAccessTime(response, currentPath);
      }
    );
  };

  const handleSubmit = async () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    setIsSubmitted(true);
    await submitAnswers();
  };

  // Show loading state
  if (isLoading) {
    return (
      <Col lg="12" className="mb-12">
        <LoadingSpinner message="Loading assessment..." fullPage={true} />
      </Col>
    );
  }

  // Show no data state
  if (!isLoading && quizQuestions.length === 0) {
    return (
      <Col lg="12" className="mb-12">
        <NoDataFound
          title="Assessment Not Found"
          message="This assessment is not available or has no questions. Please try again later."
          icon="pencil-square"
          showBackButton={true}
        />
      </Col>
    );
  }

  return (
    <Col lg="12" className="mb-12">
      {/* Back Button when not started */}
      {!timerStarted && !isSubmitted && (
        <BackButton title="Back" />
      )}

      {/* Assessment Header with Timer */}
      {quizQuestions.length > 0 && !isSubmitted && (
        <div className="assessment-header-container">
          <div className="assessment-header">
            {/* Assessment Info */}
            <div className="assessment-info">
              <h2 className="assessment-title">
                <i className="bi bi-pencil-square me-2"></i>
                {assessmentInfo?.assessmentName || 'Assessment'}
              </h2>
              <div className="assessment-meta">
                <span className="meta-item">
                  <i className="bi bi-question-circle me-1"></i>
                  {quizQuestions.length} Questions
                </span>
                <span className="meta-item">
                  <i className="bi bi-clock me-1"></i>
                  {assessmentInfo?.duration} Minutes
                </span>
              </div>
            </div>

            {/* Timer Display */}
            <div className={`timer-container ${getTimerClass()}`}>
              {!timerStarted ? (
                <div className="timer-not-started">
                  <div className="timer-icon">
                    <i className="bi bi-hourglass-split"></i>
                  </div>
                  <div className="timer-text">
                    <span className="timer-label">Duration</span>
                    <span className="timer-value">{formatTime(totalDuration)}</span>
                  </div>
                  <div className="timer-hint">
                    <i className="bi bi-info-circle me-1"></i>
                    Timer starts when you select an answer
                  </div>
                </div>
              ) : (
                <div className="timer-running">
                  <div className="timer-icon animated">
                    <i className="bi bi-stopwatch"></i>
                  </div>
                  <div className="timer-text">
                    <span className="timer-label">Time Remaining</span>
                    <span className={`timer-value ${getTimerColor()}`}>
                      {formatTime(timeRemaining)}
                    </span>
                  </div>
                  <ProgressBar 
                    now={(timeRemaining / totalDuration) * 100} 
                    variant={getTimerColor()}
                    className="timer-progress"
                  />
                </div>
              )}
            </div>
          </div>

          {/* Question Navigation */}
          {/* <div className="question-navigation">
            <span className="nav-label">Questions:</span>
            <div className="question-dots">
              {quizQuestions.map((q, i) => (
                <button
                  key={i}
                  className={`question-dot ${index === i ? 'active' : ''} ${selectedAnswers[q.id] ? 'answered' : ''}`}
                  onClick={() => goToQuestion(i)}
                >
                  {i + 1}
                </button>
              ))}
            </div>
            <div className="answered-count">
              <i className="bi bi-check-circle me-1"></i>
              {countAnsweredQuestions()}/{quizQuestions.length} answered
            </div>
          </div> */}
        </div>
      )}

      {quizQuestions.length > 0 && (
        <Card className="assessment-card">
          <Card.Body className="h-100">
            {!isSubmitted ? (
              <div className="question-container">
                {/* <div className="question-header">
                  <span className="question-number">Question {index + 1} of {quizQuestions.length}</span>
                </div> */}

                <h2 className="question-text mb-4 d-flex flex-column flex-md-row align-items-start align-items-md-center">
                  <span className="me-0 me-md-3">
                    {index + 1}. {quizQuestions[index].queston}
                  </span>

                  {quizQuestions[index].questionImage && (
                    <img
                      src={quizQuestions[index].questionImage}
                      alt="Question"
                      className="mt-2 mt-md-0 question-image"
                      style={{
                        maxWidth: "100%",
                        width: "80%",
                        height: "auto",
                        objectFit: "contain",
                        borderRadius: "6px",
                        border: "1px solid #ddd",
                      }}
                    />
                  )}
                </h2>

                <Row className="options-container">
                  {[
                    quizQuestions[index].option1,
                    quizQuestions[index].option2,
                    quizQuestions[index].option3,
                    quizQuestions[index].option4,
                  ].map((option, i) => (
                    <Col xs="12" sm="6" key={i} className="mb-3">
                      {quizQuestions[index].isImageMode === "true" ? (
                        <div 
                          className={`option-image-wrapper ${selectedAnswers[quizQuestions[index].id] === i + 1 ? 'selected' : ''}`}
                          onClick={() => handleOptionClick(quizQuestions[index].id, i + 1)}
                        >
                          <img
                            src={option}
                            alt={`Option ${i + 1}`}
                            className="option-image"
                          />
                          <div className="option-overlay">
                            <span className="option-letter">{String.fromCharCode(65 + i)}</span>
                          </div>
                        </div>
                      ) : (
                        <Button
                          variant={selectedAnswers[quizQuestions[index].id] === i + 1 ? "success" : "outline-primary"}
                          className={`option-button w-100 ${selectedAnswers[quizQuestions[index].id] === i + 1 ? 'selected' : ''}`}
                          onClick={() => handleOptionClick(quizQuestions[index].id, i + 1)}
                        >
                          <span className="option-letter-badge">{String.fromCharCode(65 + i)}</span>
                          <span className="option-text">{option}</span>
                        </Button>
                      )}
                    </Col>
                  ))}
                </Row>

                <div className="action-buttons d-flex align-items-center justify-content-between mt-4">
                  {/* <Button
                    variant="outline-secondary"
                    className="nav-button"
                    onClick={() => setIndex(Math.max(0, index - 1))}
                    disabled={index === 0}
                  >
                    <i className="bi bi-arrow-left me-2"></i>
                    Previous
                  </Button> */}

                  {index < quizQuestions.length - 1 ? (
                    <Button
                      variant="primary"
                      className="nav-button"
                      onClick={changeIndex}
                    >
                      Next
                      <i className="bi bi-arrow-right ms-2"></i>
                    </Button>
                  ) : (
                    <Button
                      variant="success"
                      className="submit-button"
                      onClick={handleSubmit}
                    >
                      <i className="bi bi-check-circle me-2"></i>
                      Submit Assessment
                    </Button>
                  )}
                </div>
              </div>
            ) : (
              <div className="results-container">
                <div className="results-header">
                  <div className="results-icon">
                    <i className="bi bi-trophy"></i>
                  </div>
                  <h2>Assessment Completed!</h2>
                  <p>Here are your results</p>
                </div>

                <div className="score-card">
                  <div className="score-item">
                    <span className="score-value">{calculateScore()}</span>
                    <span className="score-label">Correct</span>
                  </div>
                  <div className="score-divider">/</div>
                  <div className="score-item">
                    <span className="score-value">{quizQuestions.length}</span>
                    <span className="score-label">Total</span>
                  </div>
                  <div className="score-percentage">
                    {Math.round((calculateScore() / quizQuestions.length) * 100)}%
                  </div>
                </div>

                <div className="results-stats">
                  <div className="stat-box answered">
                    <i className="bi bi-check-circle"></i>
                    <span>{countAnsweredQuestions()} Answered</span>
                  </div>
                  <div className="stat-box unanswered">
                    <i className="bi bi-x-circle"></i>
                    <span>{quizQuestions.length - countAnsweredQuestions()} Unanswered</span>
                  </div>
                </div>

                <h3 className="review-title">Review Your Answers:</h3>
                {quizQuestions.map((question, i) => (
                  <div key={question.id} className="review-question mb-4">
                    <h4 className="review-question-title">
                      <span className="question-num">{i + 1}</span>
                      {question.queston}
                    </h4>
                    <Row>
                      {[question.option1, question.option2, question.option3, question.option4].map((option, j) => (
                        <Col xs="12" sm="6" key={j} className="mb-3">
                          {question.isImageMode === "true" ? (
                            <div className={`review-option-image ${j + 1 === parseInt(question.answer) ? 'correct' : selectedAnswers[question.id] === j + 1 ? 'wrong' : ''}`}>
                              <img
                                src={option}
                                alt={`Option ${j + 1}`}
                              />
                              {j + 1 === parseInt(question.answer) && (
                                <span className="correct-badge"><i className="bi bi-check-lg"></i></span>
                              )}
                            </div>
                          ) : (
                            <div className={`review-option ${j + 1 === parseInt(question.answer) ? 'correct' : selectedAnswers[question.id] === j + 1 ? 'wrong' : ''}`}>
                              <span className="option-letter-badge">{String.fromCharCode(65 + j)}</span>
                              <span className="option-text">{option}</span>
                              {j + 1 === parseInt(question.answer) && (
                                <i className="bi bi-check-circle-fill correct-icon"></i>
                              )}
                              {selectedAnswers[question.id] === j + 1 && j + 1 !== parseInt(question.answer) && (
                                <i className="bi bi-x-circle-fill wrong-icon"></i>
                              )}
                            </div>
                          )}
                        </Col>
                      ))}
                    </Row>
                  </div>
                ))}

                <div className="results-actions">
                  <Button 
                    variant="primary" 
                    onClick={() => history.push('/elearning')}
                    className="back-button"
                  >
                    <i className="bi bi-arrow-left me-2"></i>
                    Back to Dashboard
                  </Button>
                </div>
              </div>
            )}
          </Card.Body>
        </Card>
      )}

      {/* Time Up Modal */}
      <Modal
        show={showTimeUpModal}
        onHide={() => setShowTimeUpModal(false)}
        centered
        className="time-up-modal"
        backdrop="static"
      >
        <Modal.Body className="text-center p-5">
          <div className="time-up-icon">
            <i className="bi bi-alarm"></i>
          </div>
          <h2 className="time-up-title">Time's Up!</h2>
          <p className="time-up-message">
            Your assessment has been automatically submitted.
          </p>
          {isAutoSubmitting ? (
            <div className="submitting-indicator">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Submitting...</span>
              </div>
              <p>Submitting your answers...</p>
            </div>
          ) : (
            <Button 
              variant="primary" 
              onClick={() => setShowTimeUpModal(false)}
              className="view-results-btn"
            >
              <i className="bi bi-eye me-2"></i>
              View Results
            </Button>
          )}
        </Modal.Body>
      </Modal>
    </Col>
  );
};

export default AssessmentComponent;
